import { useEffect, useRef, useState } from "react";

import { TextInput } from "@components/Input";
import { Link } from "@components/Link";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { AnalyticsEvent } from "@ctypes";
import { useDebounce, useDeeplinkPromoCode, useVerifyPromotionCode } from "@hooks";
import { Translations, i18n } from "@i18n";
import { InputValidIcon } from "@routes/RedeemCodeRoute/icons";
import { Analytics } from "@services";
import { Footer, useStyles } from "./styles";
import { PromoStatus, ReferralCodeSectionProps } from "./types";

export const ReferralCodeSection = ({
  value = "",
  onChangeValue,
  ...rest
}: ReferralCodeSectionProps) => {
  const styles = useStyles();

  const promoCode = useDeeplinkPromoCode();

  const [showReferralSection, setShowReferralSection] = useState(!!promoCode);
  const [status, setStatus] = useState<PromoStatus>("idle");
  // The API's own wording for what the code is worth, preferred over the generic success line.
  const [redemptionText, setRedemptionText] = useState<string | null>(null);

  const [verifyPromotionCode, { errorMessage }] = useVerifyPromotionCode();

  const pendingCodeRef = useRef<string>("");

  const verifyCode = async (code: string) => {
    const trimmedCode = code.trim();

    if (!trimmedCode) {
      return;
    }

    pendingCodeRef.current = trimmedCode;
    setRedemptionText(null);

    try {
      const { data } = await verifyPromotionCode({
        variables: { code: trimmedCode },
      });

      if (pendingCodeRef.current !== trimmedCode) {
        return;
      }

      if (data?.verifyPromotionCode?.active) {
        Analytics.track(AnalyticsEvent.PromoCodeApplied, { code: trimmedCode });
        onChangeValue(trimmedCode);
        setRedemptionText(data.verifyPromotionCode.redemptionText ?? null);
        setStatus("success");
        return;
      }

      Analytics.track(AnalyticsEvent.PromoCodeError, { code: trimmedCode });
      setStatus("error");
    } catch {
      if (pendingCodeRef.current !== trimmedCode) {
        return;
      }

      Analytics.track(AnalyticsEvent.PromoCodeError, { code: trimmedCode });
      setStatus("error");
    }
  };

  const debouncedVerifyCode = useDebounce(verifyCode);

  /* eslint-disable react-hooks/set-state-in-effect -- async verification on promo code arrival */
  useEffect(() => {
    if (promoCode) {
      onChangeValue(promoCode);
      void verifyCode(promoCode);
    }
  }, [promoCode]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleChangeText = (newValue: string) => {
    onChangeValue(newValue);

    if (status !== "idle") {
      setStatus("idle");
    }

    debouncedVerifyCode(newValue);
  };

  if (!showReferralSection) {
    return (
      <>
        <Spacing size={SpacingSizes.M} />
        <Link
          text={i18n.t(Translations.SignUpRouteReferralCodeLink)}
          align="center"
          onClick={() => setShowReferralSection(true)}
        />
        <Spacing size={SpacingSizes.M} />
      </>
    );
  }

  const isSuccess = status === "success";

  return (
    <>
      <Spacing size={SpacingSizes.M} />
      <TextInput
        {...rest}
        label={i18n.t(Translations.SignUpRouteReferralCodeLabel)}
        value={value}
        onChangeText={handleChangeText}
        placeholder={i18n.t(Translations.SignUpRouteReferralCodePlaceholder)}
        rightIcon={isSuccess ? <InputValidIcon /> : undefined}
        error={
          status === "error"
            ? errorMessage || i18n.t(Translations.SignUpRouteReferralCodeErrorMessage)
            : undefined
        }
      />

      <Spacing size={SpacingSizes.XS} />

      {isSuccess ? (
        <Footer style={styles.successMessage}>
          {redemptionText || i18n.t(Translations.SignUpRouteReferralCodeSuccessMessage)}
        </Footer>
      ) : (
        <Footer style={styles.footer}>{i18n.t(Translations.SignUpRouteReferralCodeFooter)}</Footer>
      )}

      <Spacing size={SpacingSizes.M} />
    </>
  );
};
